import { useEffect, useRef } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { Schema } from "../types/generated";

import { tournamentDayApi } from "../api/tournamentDay";

export type TournamentLiveAudience =
  | "operator"
  | "participant"
  | "public"
  | "venue_display";
type LiveKey = {
  audience: TournamentLiveAudience;
  tournamentId: string;
  revision: number;
};

type TournamentLiveScoreMatch = {
  kind: "bracket_match" | "rubber";
  matchId: string;
};

type StreamErrorResolution = "terminal" | "rearm";
type StreamErrorListener = () =>
  | StreamErrorResolution
  | void
  | Promise<StreamErrorResolution | void>;

// Native EventSource retries on its own. This is only a short throttle before
// a later error event may trigger another authorization revalidation after a
// transport failure; it is never a timer-driven fetch.
const STREAM_ERROR_REARM_DELAY_MS = 1_000;

type BaseLiveConnectRequest = {
  credentials: "include";
  onRevision: (revision: number, reset?: boolean) => void;
  onStreamError?: StreamErrorListener;
};

type LiveConnectRequest =
  | (LiveKey & BaseLiveConnectRequest)
  | ({
      audience: "operator_score";
      match: TournamentLiveScoreMatch;
      revision: number;
      tournamentId: string;
    } & BaseLiveConnectRequest);

interface CoordinatorOptions {
  connect: (request: LiveConnectRequest) => () => void;
}

export interface TournamentLiveCoordinator {
  subscribe(
    value: string | LiveKey,
    listener: (revision: number) => void,
    onStreamError?: StreamErrorListener,
  ): () => void;
  noteRevision(value: LiveKey): void;
  activeConnections(): number;
}

type PrivateLiveOptions = {
  load?: (tournamentId: string) => Promise<unknown>;
  enabled: boolean;
  authGeneration: string | number;
  coordinator?: TournamentLiveCoordinator;
};

type PublicLiveOptions = {
  load?: (tournamentId: string) => Promise<unknown>;
  enabled: boolean;
  coordinator?: TournamentLiveCoordinator;
};

type DisplayLiveOptions = {
  load?: (
    tournamentId: string,
  ) => Promise<Schema["TournamentDisplayLiveSnapshot"]>;
  enabled: boolean;
  coordinator?: TournamentLiveCoordinator;
};

function useLiveRevisionSubscription(
  key: Omit<LiveKey, "revision">,
  revision: number | undefined,
  options: {
    enabled: boolean;
    customLoad: boolean;
    coordinator: TournamentLiveCoordinator;
    onRevision: () => void;
    onStreamError?: StreamErrorListener;
    subscriptionGeneration?: string | number;
  },
): void {
  const revisionRef = useRef(revision);
  const onRevisionRef = useRef(options.onRevision);
  const onStreamErrorRef = useRef(options.onStreamError);
  revisionRef.current = revision;
  onRevisionRef.current = options.onRevision;
  onStreamErrorRef.current = options.onStreamError;
  const canSubscribe = revision !== undefined;

  useEffect(() => {
    if (!options.enabled || options.customLoad || !canSubscribe) return;
    return options.coordinator.subscribe(
      { ...key, revision: revisionRef.current ?? 0 },
      () => onRevisionRef.current(),
      () => onStreamErrorRef.current?.(),
    );
  }, [
    canSubscribe,
    key.audience,
    key.tournamentId,
    options.coordinator,
    options.customLoad,
    options.enabled,
    options.subscriptionGeneration,
  ]);

  useEffect(() => {
    if (revision === undefined) return;
    options.coordinator.noteRevision({ ...key, revision });
  }, [key.audience, key.tournamentId, options.coordinator, revision]);
}

export function useMyTournamentLive(
  tournamentId: string,
  options: PrivateLiveOptions,
) {
  const client = useQueryClient();
  const priorGeneration = useRef(options.authGeneration);
  const queryKey = [
    "tournament-day",
    "participant",
    tournamentId,
    options.authGeneration,
  ] as const;
  const query = useQuery({
    queryKey,
    queryFn: () =>
      (options.load ?? tournamentDayApi.getMyTournamentLive)(tournamentId),
    enabled: options.enabled,
    retry: false,
  });
  useEffect(() => {
    if (priorGeneration.current === options.authGeneration) return;
    const currentKey = JSON.stringify([
      "tournament-day",
      "participant",
      tournamentId,
      options.authGeneration,
    ]);
    client.removeQueries({
      predicate: (candidate) =>
        candidate.queryKey[0] === "tournament-day" &&
        (candidate.queryKey[1] === "participant" ||
          candidate.queryKey[1] === "participant-schedule") &&
        JSON.stringify(candidate.queryKey) !== currentKey,
    });
    priorGeneration.current = options.authGeneration;
  }, [client, options.authGeneration, tournamentId]);

  const revision = (query.data as { revision?: number } | undefined)?.revision;
  const coordinator =
    options.coordinator ??
    (options.enabled
      ? getBrowserTournamentLiveCoordinator()
      : unavailableTournamentLiveCoordinator);
  useLiveRevisionSubscription(
    { audience: "participant", tournamentId },
    revision,
    {
      enabled: options.enabled,
      customLoad: options.load !== undefined,
      coordinator,
      onRevision: () => {
        void query.refetch();
      },
      subscriptionGeneration: options.authGeneration,
    },
  );
  return query;
}

export function usePublicTournamentLive(
  tournamentId: string,
  options: PublicLiveOptions,
) {
  const query = useQuery({
    queryKey: ["tournament-day", "public", tournamentId],
    queryFn: () =>
      (options.load ?? tournamentDayApi.getTournamentPublicLive)(tournamentId),
    enabled: options.enabled,
    retry: false,
  });
  const revision = (query.data as { revision?: number } | undefined)?.revision;
  const coordinator =
    options.coordinator ??
    (options.enabled
      ? getBrowserTournamentLiveCoordinator()
      : unavailableTournamentLiveCoordinator);
  useLiveRevisionSubscription({ audience: "public", tournamentId }, revision, {
    enabled: options.enabled,
    customLoad: options.load !== undefined,
    coordinator,
    onRevision: () => {
      void query.refetch();
    },
  });
  return query;
}

/** A BFF-cookie-scoped venue display is never mixed with public/player cache. */
export function useTournamentDisplayLive(
  tournamentId: string,
  options: DisplayLiveOptions,
) {
  const query = useQuery<Schema["TournamentDisplayLiveSnapshot"]>({
    queryKey: ["tournament-day", "venue-display", tournamentId],
    queryFn: () =>
      (options.load ?? tournamentDayApi.getTournamentDisplayLive)(tournamentId),
    enabled: options.enabled,
    retry: false,
  });
  const revision = query.data?.revision;
  const coordinator =
    options.coordinator ??
    (options.enabled
      ? getBrowserTournamentLiveCoordinator()
      : unavailableTournamentLiveCoordinator);
  useLiveRevisionSubscription(
    { audience: "venue_display", tournamentId },
    revision,
    {
      enabled: options.enabled,
      customLoad: options.load !== undefined,
      coordinator,
      onRevision: () => {
        void query.refetch();
      },
      // A display session is revocable while a TV is offline. An SSE reconnect
      // then fails before it can deliver a revision wake, so revalidate any
      // retained snapshot from a native stream error before rendering it.
      // snapshot with the terminal 401/403 state. The browser stream only
      // rearms after a non-terminal revalidation and a short event throttle.
      onStreamError: async () => {
        try {
          const result = await query.refetch();
          const status = (result.error as { status?: unknown } | undefined)
            ?.status;
          return status === 401 || status === 403 ? "terminal" : "rearm";
        } catch {
          return "rearm";
        }
      },
    },
  );
  return query;
}

export function useMyTournamentLiveSchedule(
  tournamentId: string,
  options: {
    enabled: boolean;
    authGeneration: string | number;
    revision: number | undefined;
  },
) {
  const client = useQueryClient();
  const queryKey = [
    "tournament-day",
    "participant-schedule",
    tournamentId,
    options.authGeneration,
    options.revision,
  ] as const;
  const currentKey = JSON.stringify(queryKey);
  const priorKey = useRef(currentKey);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      tournamentDayApi.getMyTournamentLiveSchedule(tournamentId, {
        cursor: pageParam,
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: options.enabled && options.revision !== undefined,
    retry: false,
  });
  useEffect(() => {
    if (priorKey.current === currentKey) return;
    client.removeQueries({
      predicate: (candidate) =>
        candidate.queryKey[0] === "tournament-day" &&
        candidate.queryKey[1] === "participant-schedule" &&
        JSON.stringify(candidate.queryKey) !== currentKey,
    });
    priorKey.current = currentKey;
  }, [client, currentKey]);
  return query;
}

export function usePublicTournamentLiveSchedule(
  tournamentId: string,
  options: {
    enabled: boolean;
    revision: number | undefined;
  },
) {
  const queryKey = [
    "tournament-day",
    "public-schedule",
    tournamentId,
    options.revision,
  ] as const;
  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      tournamentDayApi.getTournamentPublicLiveSchedule(tournamentId, {
        cursor: pageParam,
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: options.enabled && options.revision !== undefined,
    retry: false,
  });
}

export function createTournamentLiveCoordinator(
  options: CoordinatorOptions,
): TournamentLiveCoordinator {
  type Entry = {
    key: LiveKey;
    listeners: Set<(revision: number) => void>;
    errorListeners: Set<StreamErrorListener>;
    disconnect?: () => void;
  };
  const entries = new Map<string, Entry>();
  const identity = (value: string | LiveKey): LiveKey => {
    if (typeof value !== "string") return value;
    const [audience, tournamentId] = value.split(":") as [
      TournamentLiveAudience,
      string,
    ];
    return { audience, tournamentId, revision: 0 };
  };
  const keyOf = (value: LiveKey) => `${value.audience}:${value.tournamentId}`;
  const available = () =>
    document.visibilityState === "visible" && navigator.onLine;
  const connect = (entry: Entry) => {
    if (entry.disconnect || !available()) return;
    entry.disconnect = options.connect({
      ...entry.key,
      credentials: "include",
      onRevision: (revision, reset = false) => {
        if (!Number.isSafeInteger(revision) || revision < 0) return;
        entry.key.revision = reset
          ? revision
          : Math.max(entry.key.revision, revision);
        entry.listeners.forEach((listener) => listener(revision));
      },
      onStreamError: async () => {
        const resolutions = await Promise.all(
          [...entry.errorListeners].map(async (listener) => {
            try {
              return await listener();
            } catch {
              return "rearm" as const;
            }
          }),
        );
        if (resolutions.some((resolution) => resolution === "terminal")) {
          return "terminal" as const;
        }
        return resolutions.some((resolution) => resolution === "rearm")
          ? ("rearm" as const)
          : undefined;
      },
    });
  };
  const disconnectAll = () => {
    entries.forEach((entry) => {
      entry.disconnect?.();
      entry.disconnect = undefined;
    });
  };
  const reconnect = () => {
    if (!available()) return;
    entries.forEach(connect);
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") reconnect();
    else disconnectAll();
  });
  window.addEventListener("online", reconnect);
  window.addEventListener("offline", disconnectAll);

  return {
    subscribe(
      value: string | LiveKey,
      listener: (revision: number) => void,
      onStreamError?: StreamErrorListener,
    ) {
      const liveKey = identity(value);
      const cacheKey = keyOf(liveKey);
      let entry = entries.get(cacheKey);
      if (!entry) {
        entry = {
          key: liveKey,
          listeners: new Set(),
          errorListeners: new Set(),
        };
        entries.set(cacheKey, entry);
      } else {
        entry.key.revision = Math.max(entry.key.revision, liveKey.revision);
      }
      entry.listeners.add(listener);
      if (onStreamError) entry.errorListeners.add(onStreamError);
      connect(entry);
      return () => {
        entry?.listeners.delete(listener);
        if (onStreamError) entry?.errorListeners.delete(onStreamError);
        if (entry?.listeners.size === 0) {
          entry.disconnect?.();
          entries.delete(cacheKey);
        }
      };
    },
    noteRevision(value: LiveKey) {
      const entry = entries.get(keyOf(value));
      if (entry) {
        entry.key.revision = Math.max(entry.key.revision, value.revision);
      }
    },
    activeConnections: () =>
      [...entries.values()].filter((entry) => entry.disconnect !== undefined)
        .length,
  };
}

export function connectBrowserTournamentLive(
  request: LiveConnectRequest,
): () => void {
  if (typeof EventSource === "undefined") {
    return () => undefined;
  }
  const query = new URLSearchParams({
    after_revision: String(request.revision),
  });
  const path =
    request.audience === "public"
      ? `/bff/api/tournaments/${encodeURIComponent(request.tournamentId)}/live/public/events?${query}`
      : request.audience === "venue_display"
        ? `/bff/api/tournaments/${encodeURIComponent(request.tournamentId)}/live/display/events?${query}`
        : request.audience === "operator_score"
          ? `/bff/api/tournaments/${encodeURIComponent(request.tournamentId)}/live/operator/matches/${request.match.kind}/${encodeURIComponent(request.match.matchId)}/score-events?${query}`
          : `/bff/api/tournaments/${encodeURIComponent(request.tournamentId)}/live/events?audience=${encodeURIComponent(request.audience)}&${query}`;
  let source: EventSource;
  try {
    source = new EventSource(path, { withCredentials: true });
  } catch {
    return () => undefined;
  }
  // Browsers automatically retry EventSource failures. Only surface the first
  // error for a connection epoch; otherwise a 401/403 (or temporary network
  // outage) would turn into polling. A successful `open` starts a new epoch.
  let streamErrorReported = false;
  let rearmTimer: number | undefined;
  const clearRearmTimer = () => {
    if (rearmTimer === undefined) return;
    window.clearTimeout(rearmTimer);
    rearmTimer = undefined;
  };
  const rearmAfterTransientFailure = () => {
    clearRearmTimer();
    rearmTimer = window.setTimeout(() => {
      streamErrorReported = false;
      rearmTimer = undefined;
    }, STREAM_ERROR_REARM_DELAY_MS);
  };
  source.addEventListener("open", () => {
    clearRearmTimer();
    streamErrorReported = false;
  });
  source.addEventListener("error", () => {
    if (streamErrorReported) return;
    streamErrorReported = true;
    if (!request.onStreamError) return;
    void Promise.resolve(request.onStreamError())
      .then((resolution) => {
        if (resolution === "rearm") rearmAfterTransientFailure();
      })
      .catch(rearmAfterTransientFailure);
  });
  const onWake = (event: Event) => {
    const message = event as MessageEvent<string>;
    let revision = message.lastEventId
      ? Number(message.lastEventId)
      : Number.NaN;
    if (!Number.isSafeInteger(revision) || revision < 0) {
      try {
        revision = Number(JSON.parse(message.data).revision);
      } catch {
        return;
      }
    }
    if (Number.isSafeInteger(revision) && revision >= 0) {
      if (message.type === "snapshot") {
        request.onRevision(revision, true);
      } else {
        request.onRevision(revision);
      }
    }
  };
  source.addEventListener("revision", onWake);
  source.addEventListener("snapshot", onWake);
  return () => {
    clearRearmTimer();
    source.close();
  };
}

const unavailableTournamentLiveCoordinator: TournamentLiveCoordinator = {
  subscribe: () => () => undefined,
  noteRevision: () => undefined,
  activeConnections: () => 0,
};
let browserTournamentLiveCoordinator: TournamentLiveCoordinator | undefined;

function getBrowserTournamentLiveCoordinator(): TournamentLiveCoordinator {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return unavailableTournamentLiveCoordinator;
  }
  if (!browserTournamentLiveCoordinator) {
    browserTournamentLiveCoordinator = createTournamentLiveCoordinator({
      connect: connectBrowserTournamentLive,
    });
  }
  return browserTournamentLiveCoordinator;
}
